const ALLOWED_HTML_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'h2',
    'h3',
    'i',
    'img',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strong',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
])

const ALLOWED_HTML_ATTRS = {
    a: new Set(['href', 'target', 'rel']),
    img: new Set(['src', 'alt', 'title', 'width', 'height']),
    table: new Set(['border', 'cellspacing', 'cellpadding']),
    '*': new Set(['colspan', 'rowspan']),
}

const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:']
const SAFE_IMAGE_PROTOCOLS = ['http:', 'https:', 'data:']
const SAFE_STYLE_RULES = new Set([
    'background-color',
    'border',
    'border-collapse',
    'color',
    'font-size',
    'max-width',
    'padding',
    'text-align',
    'text-decoration',
    'white-space',
    'width',
])

function sanitizeInlineStyle (styleValue) {
    if (!styleValue) {
        return ''
    }

    return styleValue
        .split(';')
        .map((rule) => rule.trim())
        .filter(Boolean)
        .map((rule) => {
            const separatorIndex = rule.indexOf(':')

            if (separatorIndex === -1) {
                return null
            }

            const property = rule.slice(0, separatorIndex).trim().toLowerCase()
            const value = rule.slice(separatorIndex + 1).trim()

            if (!SAFE_STYLE_RULES.has(property)) {
                return null
            }

            if (!value || /expression|url\s*\(|javascript:/i.test(value)) {
                return null
            }

            return `${property}: ${value}`
        })
        .filter(Boolean)
        .join('; ')
}

export function makeSafeHtml (html) {
    if (!html || typeof window === 'undefined') {
        return ''
    }

    const parser = new window.DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const sanitizeNode = (node) => {
        Array.from(node.children).forEach((child) => {
            const tagName = child.tagName.toLowerCase()

            if (!ALLOWED_HTML_TAGS.has(tagName)) {
                child.replaceWith(...Array.from(child.childNodes))
                return
            }

            Array.from(child.attributes).forEach((attr) => {
                const attrName = attr.name.toLowerCase()
                const allowedAttrs = ALLOWED_HTML_ATTRS[tagName] || new Set()
                const isAllowed =
                    allowedAttrs.has(attrName) ||
                    ALLOWED_HTML_ATTRS['*'].has(attrName) ||
                    attrName === 'style' ||
                    attrName === 'class'

                if (!isAllowed || attrName.startsWith('on')) {
                    child.removeAttribute(attr.name)
                    return
                }

                if (tagName === 'a' && attrName === 'href') {
                    try {
                        const url = new URL(attr.value, window.location.origin)

                        if (!SAFE_LINK_PROTOCOLS.includes(url.protocol)) {
                            child.removeAttribute(attr.name)
                        }
                    } catch (e) {
                        child.removeAttribute(attr.name)
                    }
                }

                if (tagName === 'img' && attrName === 'src') {
                    try {
                        const url = new URL(attr.value, window.location.origin)

                        if (!SAFE_IMAGE_PROTOCOLS.includes(url.protocol)) {
                            child.removeAttribute(attr.name)
                        }
                    } catch (e) {
                        child.removeAttribute(attr.name)
                    }
                }

                if (attrName === 'style') {
                    const safeStyle = sanitizeInlineStyle(attr.value)

                    if (safeStyle) {
                        child.setAttribute('style', safeStyle)
                    } else {
                        child.removeAttribute('style')
                    }
                }

                if (attrName === 'class') {
                    const safeClassName = attr.value
                        .split(/\s+/)
                        .map((className) => className.trim())
                        .filter(Boolean)
                        .filter((className) => /^[a-zA-Z0-9_-]+$/.test(className))
                        .join(' ')

                    if (safeClassName) {
                        child.setAttribute('class', safeClassName)
                    } else {
                        child.removeAttribute('class')
                    }
                }
            })

            if (tagName === 'a' && child.getAttribute('target') === '_blank') {
                child.setAttribute('rel', 'noopener noreferrer')
            }

            sanitizeNode(child)
        })
    }

    sanitizeNode(doc.body)

    return doc.body.innerHTML
}

export default makeSafeHtml
